/**
 * Epoch Key Cache — 3-tier CSK resolution for CSK rotation.
 *
 * Tiers:
 * 1. Hot cache (in-memory, plaintext CSKs) — fastest, cleared on logout
 * 2. Warm cache (IndexedDB, self-encapsulated eCSKs) — persistent across reloads
 * 3. Server fetch (API call) — fallback for cache misses
 *
 * See csk-rotation-spec.md §8 for full design.
 */

import { db } from "@/indexedDB/db";
import { StoredEpochPayload } from "@/indexedDB/types";
import {
  decapsulateSecurePayload,
  encapsulateSecurePayload,
} from "@/lib/crypto/crypto";
import { isUserAuthenticated } from "./auth";
import { getOrDecryptUserPK } from "./cryptoCache";
import { fetchEpoch, fetchEpochs } from "@/server/services/epochService";

// ---------------------------------------------------------------------------
// Hot cache (in-memory, plaintext CSKs)
// ---------------------------------------------------------------------------

// Map<chatId, Map<epoch, csk>>
const hotCache: Map<string, Map<number, string>> = new Map();

export const getCachedCSK = (
  chatId: string,
  epoch: number,
): string | undefined => {
  return hotCache.get(chatId)?.get(epoch);
};

export const setCachedCSK = (
  chatId: string,
  epoch: number,
  csk: string,
): void => {
  if (!hotCache.has(chatId)) {
    hotCache.set(chatId, new Map());
  }
  hotCache.get(chatId)!.set(epoch, csk);
};

export const clearChatHotCache = (chatId: string): void => {
  hotCache.delete(chatId);
};

/**
 * Debug-only: hot-cache footprint for a single chat. UTF-16 → 2 bytes/char.
 */
export const getChatHotCacheStats = (
  chatId: string,
): { entries: number; bytes: number } => {
  const perChat = hotCache.get(chatId);
  if (!perChat) {
    return { entries: 0, bytes: 0 };
  }
  let bytes = chatId.length * 2;
  for (const [, csk] of perChat.entries()) {
    bytes += 8 + csk.length * 2; // ~8 bytes for the epoch number + key string
  }
  return { entries: perChat.size, bytes };
};

export const clearAllHotCache = (): void => {
  hotCache.clear();
};

// ---------------------------------------------------------------------------
// Warm cache (IndexedDB — self-encapsulated eCSKs)
// ---------------------------------------------------------------------------

const makeEpochPayloadId = (chatId: string, epoch: number): string =>
  `${chatId}:${epoch}`;

/**
 * Read a self-encapsulated eCSK from IndexedDB for a specific chat + epoch.
 */
export const getStoredEpochPayload = async (
  chatId: string,
  epoch: number,
): Promise<string | undefined> => {
  const record = await db.epochPayloads.get(makeEpochPayloadId(chatId, epoch));
  return record?.selfECSK;
};

/**
 * Batch-write self-encapsulated eCSK payloads to IndexedDB.
 */
export const storeEpochPayloads = async (
  chatId: string,
  payloads: Array<{ epoch: number; selfECSK: string }>,
): Promise<void> => {
  const now = Date.now();
  const records: StoredEpochPayload[] = payloads.map(({ epoch, selfECSK }) => ({
    id: makeEpochPayloadId(chatId, epoch),
    chatId,
    epoch,
    selfECSK,
    storedAt: now,
  }));
  await db.epochPayloads.bulkPut(records);
};

/**
 * Clear all warm-cache entries for a specific chat.
 */
export const clearChatWarmCache = async (chatId: string): Promise<void> => {
  await db.epochPayloads.where("chatId").equals(chatId).delete();
};

/**
 * Clear the entire warm cache (account deletion only).
 */
export const clearAllWarmCache = async (): Promise<void> => {
  await db.epochPayloads.clear();
};

// ---------------------------------------------------------------------------
// Self-encapsulation helper
// ---------------------------------------------------------------------------

/**
 * Re-encapsulate a plaintext CSK for the current user's own public key.
 * The result can be stored in IndexedDB as a "self-eCSK" and later
 * decapsulated with the user's private key.
 */
export const selfEncapsulateCSK = async (csk: string): Promise<string> => {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present — cannot self-encapsulate");
  }

  const userPK = await getOrDecryptUserPK({
    username: userFingerPrint.username,
    dk: userFingerPrint.dk,
    ePrK: userFingerPrint.ePrK,
  });

  const selfPayload = encapsulateSecurePayload({
    receiverPublicKeys: userFingerPrint.ePuK,
    senderPrivateKeys: userPK,
    chatSecretKey: csk,
  });

  return selfPayload;
};

// ---------------------------------------------------------------------------
// Custom error for purged epochs
// ---------------------------------------------------------------------------

export class EpochPurgedError extends Error {
  chatId: string;
  epoch: number;
  constructor(chatId: string, epoch: number) {
    super(
      `Epoch ${epoch} for chat ${chatId} has been purged and is unavailable`,
    );
    this.name = "EpochPurgedError";
    this.chatId = chatId;
    this.epoch = epoch;
  }
}

// ---------------------------------------------------------------------------
// 3-tier CSK resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the plaintext CSK for a given chat + epoch.
 *
 * Resolution order:
 * 1. Hot cache (in-memory) — instant
 * 2. Inline eCSK (from API response) — decapsulate + cache
 * 3. Warm cache (IndexedDB, self-encapsulated) — decapsulate + cache
 * 4. Server fetch — fetch epoch record, extract user's eCSK, decapsulate + cache
 *
 * @param chatId  The chat ID
 * @param epoch   The key epoch number (0 = original CSK)
 * @param eCSK    Optional — if provided (e.g., inline from API), used directly
 */
export const resolveCSK = async (
  chatId: string,
  epoch: number,
  eCSK?: string,
): Promise<string> => {
  // 1. Hot cache hit
  const cached = getCachedCSK(chatId, epoch);
  if (cached) {
    return cached;
  }

  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present — cannot resolve CSK");
  }

  const userPK = await getOrDecryptUserPK({
    username: userFingerPrint.username,
    dk: userFingerPrint.dk,
    ePrK: userFingerPrint.ePrK,
  });

  let csk: string;

  if (eCSK) {
    // 2. Inline eCSK provided — decapsulate directly
    csk = decapsulateSecurePayload({
      privateKeys: userPK,
      securePayload: eCSK,
    });
    // Persist to warm cache
    const selfPayload = await selfEncapsulateCSK(csk);
    await storeEpochPayloads(chatId, [{ epoch, selfECSK: selfPayload }]);
  } else {
    // 3. Warm cache (IndexedDB)
    const selfPayload = await getStoredEpochPayload(chatId, epoch);
    if (selfPayload) {
      csk = decapsulateSecurePayload({
        privateKeys: userPK,
        securePayload: selfPayload,
      });
    } else {
      // 4. Server fetch
      try {
        const epochRecord = await fetchEpoch(chatId, epoch);
        const serverPayload = epochRecord.self_payload;
        if (!serverPayload) {
          throw new EpochPurgedError(chatId, epoch);
        }
        csk = decapsulateSecurePayload({
          privateKeys: userPK,
          securePayload: serverPayload,
        });
        // Persist to warm cache
        const selfWrapped = await selfEncapsulateCSK(csk);
        await storeEpochPayloads(chatId, [{ epoch, selfECSK: selfWrapped }]);
      } catch (error) {
        if (error instanceof EpochPurgedError) {
          throw error;
        }
        throw new EpochPurgedError(chatId, epoch);
      }
    }
  }

  // 5. Store in hot cache
  setCachedCSK(chatId, epoch, csk);
  return csk;
};

// ---------------------------------------------------------------------------
// Batch processing of inline epoch_payloads from paginated message responses
// ---------------------------------------------------------------------------

/**
 * Processes `epoch_payloads` returned inline with paginated message responses.
 * For each epoch → eCSK entry, decapsulates the CSK, self-encapsulates it,
 * and stores it in the warm cache so future decryptions skip the server.
 *
 * Safe to call with undefined/empty payloads (no-op).
 */
export const processInlineEpochPayloads = async (
  chatId: string,
  epochPayloads?: Record<number, string>,
): Promise<void> => {
  if (!epochPayloads || Object.keys(epochPayloads).length === 0) {
    return;
  }

  const auth = isUserAuthenticated();
  if (!auth) {
    return;
  }

  const userPK = await getOrDecryptUserPK({
    username: auth.username,
    dk: auth.dk,
    ePrK: auth.ePrK,
  });

  const payloadsToStore: Array<{ epoch: number; selfECSK: string }> = [];

  for (const [epochStr, eCSK] of Object.entries(epochPayloads)) {
    const epoch = Number(epochStr);

    // Skip if already in warm cache
    const existing = await getStoredEpochPayload(chatId, epoch);
    if (existing) {
      continue;
    }

    try {
      // Decapsulate the server-provided eCSK → plaintext CSK
      const csk = decapsulateSecurePayload({
        privateKeys: userPK,
        securePayload: eCSK,
      });

      // Put in hot cache
      setCachedCSK(chatId, epoch, csk);

      // Self-encapsulate for warm cache
      const selfPayload = await selfEncapsulateCSK(csk);
      payloadsToStore.push({ epoch, selfECSK: selfPayload });
    } catch {
      // Skip epochs that fail to decapsulate (e.g. purged or corrupt)
      continue;
    }
  }

  if (payloadsToStore.length > 0) {
    await storeEpochPayloads(chatId, payloadsToStore);
  }
};

// ---------------------------------------------------------------------------
// Cache cleanup
// ---------------------------------------------------------------------------

/**
 * Clears the hot cache (in-memory plaintext CSKs).
 * Called on logout. The warm cache (IndexedDB) is intentionally retained —
 * self-encapsulated keys are useless without re-authentication.
 */
export const clearHotCache = (): void => {
  hotCache.clear();
};

/**
 * Clears the warm cache (IndexedDB epoch payloads).
 * Called ONLY on account deletion — not on regular logout.
 */
export const clearWarmCache = async (): Promise<void> => {
  try {
    await db.epochPayloads.clear();
  } catch {
    // IndexedDB may already be deleted during logout
  }
};

// ---------------------------------------------------------------------------
// Reconnect epoch sync (spec §11.2, Option A — Bulk Epoch Catch-Up)
// ---------------------------------------------------------------------------

/**
 * Syncs epoch keys for a single chat after reconnect.
 *
 * Fetches all available epochs from the server that are newer than
 * `localEpoch`, decapsulates them, and stores in warm + hot caches.
 * Also updates the local chat entity's `currentEpoch` if it has advanced.
 *
 * @param chatId     — target chat
 * @param localEpoch — the epoch the local client currently knows about
 */
export const syncEpochsForChat = async (
  chatId: string,
  localEpoch: number,
): Promise<void> => {
  const auth = isUserAuthenticated();
  if (!auth) {
    return;
  }

  try {
    const allEpochs = await fetchEpochs(chatId);
    // Filter to only epochs newer than what we have locally
    const newEpochs = allEpochs.filter((e) => e.epoch > localEpoch);
    if (newEpochs.length === 0) {
      return;
    }

    const userPK = await getOrDecryptUserPK({
      username: auth.username,
      dk: auth.dk,
      ePrK: auth.ePrK,
    });

    const payloadsToStore: Array<{ epoch: number; selfECSK: string }> = [];

    for (const epochRecord of newEpochs) {
      const ownECSK = epochRecord.self_payload;
      if (!ownECSK) {
        continue;
      } // We may have been removed before this epoch

      // Skip if already in warm cache
      const existing = await getStoredEpochPayload(chatId, epochRecord.epoch);
      if (existing) {
        continue;
      }

      try {
        const csk = decapsulateSecurePayload({
          privateKeys: userPK,
          securePayload: ownECSK,
        });

        setCachedCSK(chatId, epochRecord.epoch, csk);

        const selfPayload = await selfEncapsulateCSK(csk);
        payloadsToStore.push({
          epoch: epochRecord.epoch,
          selfECSK: selfPayload,
        });
      } catch {
        continue;
      }
    }

    if (payloadsToStore.length > 0) {
      await storeEpochPayloads(chatId, payloadsToStore);
    }
  } catch (error) {
    console.warn(`Epoch sync failed for chat ${chatId}:`, error);
  }
};
